import type { AtTag } from "../util/tag.js";
import type { AbsolutePath, Item, ValidatorType } from "shelving";
import { ALLOW_STRING, BOOLEAN, BooleanSchema, DATA, INTEGER, KEY, NON_NEGATIVE_INTEGER, OPTIONAL, OPTIONAL_KEY, OPTIONAL_TIMESTAMP, STRING, StringSchema, TIMESTAMP, isProp, pickProps } from "shelving";
import { KEYWORD_FIELDS } from "../util/keyword.js";
import { IMAGES, OPTIONAL_COVER, OPTIONAL_PHOTO } from "../util/media.js";
import { APPROVED_PRIVACIES, PRIVATE_PRIVACIES, PUBLIC_PRIVACIES, type PrivacyTypes } from "../util/privacy.js";
import { CREATOR_ENTITY, type CreatorEntity, ENTITIES } from "./entity.js";
import { ABOUT, CONTENT, HUE, OPTIONAL_LOCATION, OPTIONAL_WEBSITE } from "./misc.js";
import { UsernameSchema } from "./username.js";

// Constants.
export const GROUPS_MAX = 20;
export const MESSAGES_MAX = 50;
export const MEMBERS_MAX = 50;
export const GROUP_USERNAME_MIN_MEMBERS = 20; // Groups need more than this to create a username.

// Member types.
export const MEMBER_OWNER_STATUS = {
	owner: "Owner",
};
export const MEMBER_ADMIN_STATUS = {
	...MEMBER_OWNER_STATUS,
	admin: "Admin",
};
export const MEMBER_APPROVED_STATUS = {
	...MEMBER_ADMIN_STATUS,
	member: "Member",
};
export const MEMBER_BLOCKED_STATUS = {
	blocked: "Blocked",
};
export const MEMBER_STATUS = {
	...MEMBER_APPROVED_STATUS,
	invited: "Invited",
	pending: "Pending",
	...MEMBER_BLOCKED_STATUS,
};
export type MemberOwnerStatus = keyof typeof MEMBER_OWNER_STATUS;
export type MemberAdminStatus = keyof typeof MEMBER_ADMIN_STATUS;
export type MemberApprovedStatus = keyof typeof MEMBER_APPROVED_STATUS;
export type MemberBlockedStatus = keyof typeof MEMBER_BLOCKED_STATUS;
export type MemberStatus = keyof typeof MEMBER_STATUS;

export const isAdminMember = (status: MemberStatus | "none"): status is MemberAdminStatus => isProp(MEMBER_ADMIN_STATUS, status);
export const isOwnerMember = (status: MemberStatus | "none"): status is MemberOwnerStatus => isProp(MEMBER_OWNER_STATUS, status);
export const isApprovedMember = (status: MemberStatus | "none"): status is MemberApprovedStatus => isProp(MEMBER_APPROVED_STATUS, status);
export const isBlockedMember = (status: MemberStatus | "none"): status is MemberBlockedStatus => isProp(MEMBER_BLOCKED_STATUS, status);

export const canGroupHaveUsername = ({ verified = false, members = 0, username = null }: Partial<GroupData>): boolean => !!username || verified || members >= GROUP_USERNAME_MIN_MEMBERS;

export function getMemberAllowedPrivacyTypes({ status }: MemberData | NonMemberData): PrivacyTypes {
	return isAdminMember(status) ? PRIVATE_PRIVACIES : isApprovedMember(status) ? APPROVED_PRIVACIES : PUBLIC_PRIVACIES;
}

/** Group data. */
export const GROUP_SCHEMA = DATA({
	verified: BOOLEAN,
	username: OPTIONAL(new UsernameSchema({ description: `Groups with more than ${GROUP_USERNAME_MIN_MEMBERS} members can set a unique username.` })), // Username set if this is a clan that is large enough to allow a username.
	name: new StringSchema({ title: "Name", min: 1, max: 40 }),
	about: ABOUT,
	tags: ENTITIES,
	location: OPTIONAL_LOCATION,
	website: OPTIONAL_WEBSITE,
	photo: OPTIONAL_PHOTO,
	cover: OPTIONAL_COVER,
	hue: HUE,
	open: new BooleanSchema({ title: "Open group", description: "Allow people to join this group without being invited.", value: false }),
	approval: new BooleanSchema({ title: "Approve new members", description: "Member join requests must be approved by an admin of this group.", value: true }),
	created: INTEGER, // Date the group was created.
	members: INTEGER, // Number of members in the group.
	...KEYWORD_FIELDS,
});
export type GroupData = ValidatorType<typeof GROUP_SCHEMA>;

/** Form to create or edit a group. */
export const GROUP_FORM_SCHEMA = DATA(pickProps(GROUP_SCHEMA.props, "username", "name", "about", "location", "website", "open", "approval", "photo", "cover", "hue"));
export type GroupFormData = ValidatorType<typeof GROUP_FORM_SCHEMA>;

/** Get the URL path for a group (uses the username if there is one or `/group/{id}` otherwise). */
export const getGroupPath = ({ id: gid, username }: Item<GroupData>): AbsolutePath => (username ? `/${username}` : `/group/${gid}`);

/** Get the `@tag` or `group:tag` for a group (uses the username if there is one or entity reference otherwise). */
export const getGroupTag = ({ id: gid, username }: Item<GroupData>): CreatorEntity | AtTag => (username ? `@${username}` : `group:${gid}`);

/** Get the title for a group. */
export const getGroupTitle = ({ name, username }: GroupData): string => (username ? `${name} (@${username})` : name);

/**
 * Members: members of groups
 * - Stored at `groups/{gid}:{uid}`
 * - If `status` is 1 or more this member is said to have 'membership' and can see members and posts in this group.
 */
export const MEMBER_SCHEMA = DATA({
	gid: KEY, // Link to group.
	uid: KEY, // Link to user.
	status: ALLOW_STRING(MEMBER_STATUS),
	updated: INTEGER, // Date of the last status change.
	updater: KEY, // ID of the user who is responsible for the last status change.
	why: STRING, // Reason for the last status change.
	muted: OPTIONAL_TIMESTAMP, // When the user is muted until.
});
export type MemberData = ValidatorType<typeof MEMBER_SCHEMA>;
export type NonMemberData = { status: "none" } & { [K in Exclude<keyof MemberData, "status">]?: undefined };

/** Object representing a non-member of a group. */
export const NON_MEMBER: NonMemberData = { status: "none" };

/** Get the ID of a Member document (recommend using this because it ensures `uid` and `gid` are the correct way round). */
export const getMemberID = (gid: string, uid: string): string => `${uid}:${gid}`;

/** Get the ID of the `assigned` list for a group. */
export const getAssignedID = (gid: string): string => `${gid}:assigned`;

/**
 * Messages: stores messages in groups.
 * - Stored at `posts/{random}`
 */
export const MESSAGE_SCHEMA = DATA({
	creator: CREATOR_ENTITY, // Entity of the user who wrote this message.
	gid: KEY, // ID of the group this message is in.
	date: TIMESTAMP,
	thread: OPTIONAL_KEY, // ID of the message this message is replying to.
	replies: NON_NEGATIVE_INTEGER, // Number of replies.
	images: IMAGES,
	content: CONTENT,
	tags: ENTITIES, // Any `@tags` or `#tags` found in `content`
	links: ENTITIES, // List of linked entites, e.g. the challenge this message relates to.
	reactions: INTEGER, // Count the number of different types of reactions. */
});
export type MessageData = ValidatorType<typeof MESSAGE_SCHEMA>;

/** Create a message. */
export const MESSAGE_FORM_SCHEMA = DATA(pickProps(MESSAGE_SCHEMA.props, "content", "images", "links"));
